import { Injectable, inject } from '@angular/core';
import QWebChannelModule from 'qwebchannel';
const QWebChannel = (QWebChannelModule as any).QWebChannel;
import { SecretsService } from '../secrets/secrets.service';
import { ConnectionService } from '../connection/connection.service';
import { ObjectStorageService } from '../object-storage/object-storage.service';
import { Connection, ConnectionType } from '../../interfaces';
import { ObjectStorageProvider, type ObjectStorageConnection } from '../../interfaces/object-storage.interface';
import { firstValueFrom } from 'rxjs';
import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';
import { SimpleUIService } from '../simple-ui/simple-ui.service';
import type { StepParams } from '@enlighten/ts-shared/step-api';

type CameraSelection = [string]; // Camera name
type PipelinePreviewRequest = [string, string, string, number, number]; // [inputVideoPath, pipelineName, textPrompt, startFrameNumber, endFrameNumber]
type PipelineProcessRequest = [string, string, string, string, number, number]; // [inputVideoPath, outputVideoPath, pipelineName, textPrompt, startFrameNumber, endFrameNumber]
type PipelinePreviewCameraRequest = [string, string, string]; // [cameraName, pipelineName, textPrompt]
type PipelineRecordCameraRequest = [string, string, string, string]; // [cameraName, outputVideoPath, pipelineName, textPrompt]
type CloudOperationRequest = [string, string]; // [connectionName, fileName]
type CameraRecordJob = [string, string]; // [cameraName, fileName]
type TrackingRequest = [string, string, string]; // [cameraName, refCameraName, objectClass]
type CameraCloudRecordJob = [string, string]; // [cameraName, connectionName]
type ProxyUrlRequest = [string]; // [url]
type ConnectionConfig = {
  type: string;
  endpoint: string;
  accessKeyId: string;
  secretAccessKey: string;
  region: string;
  bucket: string;
};
const ZCAM_CONNECTION_TYPE = "ZCamHTTP";

declare global {
  interface Window {
    ehq_loaded: boolean | undefined;
    webkit?: {
      messageHandlers: {
        [x: string]: {
          postMessage: (message: any) => Promise<string>;
        };
      }
    };
    ehq_client?: {
      takePhoto: (camera: CameraSelection) => Promise<string>;
      getCameraList: () => Promise<string[]>;
      selectCamera: (selection: CameraSelection) => Promise<string>;
      releaseCamera: (selection: CameraSelection) => Promise<string>;
      selectROI: () => Promise<string>;
      startVideoRecording: (recordJob: CameraRecordJob) => Promise<string>;
      stopVideoRecording: (camera: CameraSelection) => Promise<string>;
      uploadVideo: (request: CloudOperationRequest) => Promise<string>;
      downloadVideo: (request: CloudOperationRequest) => Promise<string>;
      pipelinePreview: (request: PipelinePreviewRequest) => Promise<string>;
      pipelineProcess: (request: PipelineProcessRequest) => Promise<string>;
      pipelinePreviewCamera: (request: PipelinePreviewCameraRequest) => Promise<string>;
      pipelineRecordCamera: (request: PipelineRecordCameraRequest) => Promise<string>;
      pipelineStopCamera: (request: CameraSelection) => Promise<string>;
      startTracking: (request: TrackingRequest) => Promise<string>;
      stopTracking: (request: TrackingRequest) => Promise<string>;
      startCloudRecording: (request: CameraCloudRecordJob) => Promise<string>;
      stopCloudRecording: (request: CameraSelection) => Promise<string>;
      proxyUrlForSource(request: ProxyUrlRequest): Promise<string>;
    };
    qt?: {
      webChannelTransport: any;
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class FatClientService {
  private secretsService = inject(SecretsService);
  private connectionService = inject(ConnectionService);
  private objectStorageService = inject(ObjectStorageService);
  private simpleUIService = inject(SimpleUIService);


  private isQtEnvironmentAvailable(): boolean {
    return typeof window.qt !== "undefined" && typeof window.qt.webChannelTransport !== "undefined";
  }

  private async isQWebChannelAvailable(): Promise<boolean> {
    return !!QWebChannel;
  }

  private createQWebChannelPromise<T>(
    bridgeSetup: (bridge: any, reject?: (reason?: any) => void) => void,
    responseHandler: (data: string) => T,
    useJsBinHandler = false
  ): Promise<T> {
    if (!this.isQtEnvironmentAvailable()) {
      return Promise.reject(new Error('Native bridge not available in this environment.'));
    }
    // Check QWebChannel availability using then/catch
    return new Promise((resolve, reject) => {
      this.isQWebChannelAvailable()
        .then((available) => {
          if (!available) {
            reject(new Error('QWebChannel not available'));
            return;
          }
          this.setupQWebChannel(resolve, responseHandler, bridgeSetup, useJsBinHandler, reject);
        })
        .catch((err) => {
          reject(new Error('QWebChannel check failed: ' + err));
        });
    });
  }

  private setupQWebChannel<T>(
    resolve: (value: T | PromiseLike<T>) => void,
    responseHandler: (data: string) => T,
    bridgeSetup: (bridge: any, reject?: (reason?: any) => void) => void,
    useJsBinHandler: boolean,
    reject?: (reason?: any) => void
  ) {
    const channel = new QWebChannel(window.qt!.webChannelTransport, (channel: any) => {
      const bridge = channel.objects.bridge;
      const handler = useJsBinHandler ? bridge.sendToJsBin : bridge.sendToJs;
      handler.connect((data: string) => {
        resolve(responseHandler(data));
      });
      bridgeSetup(bridge, reject);
    });
    void channel;
  }

  private async getConnectionByName(projectId: number, connectionName: string): Promise<Connection> {
    const connections = await firstValueFrom(this.connectionService.getConnections(projectId));
    const connection = connections.find(conn => {
      const connName = typeof conn.name === 'string' ? conn.name : String(conn.name);
      return connName.trim().toLowerCase() === String(connectionName).trim().toLowerCase();
    });

    if (!connection) {
      throw new Error(`Connection with name ${connectionName} not found`);
    }
    if (connection.type !== ConnectionType.ObjectStorage) {
      throw new Error(`Connection with name ${connectionName} is not of type ObjectStorage`);
    }

    return connection;
  }

  private async getConnectionConfig(name: string): Promise<ConnectionConfig> {
    try {
      if (name && name.length > 0) {
        const projectId = this.connectionService.getCurrentProjectId();
        const connection = await this.getConnectionByName(projectId, name);
        if (!connection) {
          throw new Error(`Connection ${name} not found`);
        }
        return this.getObjectStorageConfig(projectId, connection);
      } else {
        const config: ConnectionConfig = {
          type: ZCAM_CONNECTION_TYPE,
          endpoint: '',
          accessKeyId: '',
          secretAccessKey: '',
          region: '',
          bucket: '',
        };
        return config;
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Build the storage ConnectionConfig for an ObjectStorage connection.
   * Credentials live on the dedicated ObjectStorage record (not on Connection.config);
   * the secret access key is resolved from the connection's secretId via SecretsService.
   */
  private async getObjectStorageConfig(projectId: number, connection: Connection): Promise<ConnectionConfig> {
    if (connection.connectionId == null) {
      throw new Error(`ObjectStorage connection ${connection.name} has no connectionId`);
    }
    const objectStorage = await firstValueFrom(
      this.objectStorageService.get(projectId, connection.connectionId)
    );
    return this.buildObjectStorageConfig(projectId, objectStorage);
  }

  /**
   * Turn an ObjectStorage record into the bridge ConnectionConfig, resolving the
   * secret access key from the connection's secretId via SecretsService.
   */
  private async buildObjectStorageConfig(projectId: number, objectStorage: ObjectStorageConnection): Promise<ConnectionConfig> {
    let secretAccessKey = '';
    if (objectStorage.secretId != null) {
      const secret = await firstValueFrom(
        this.secretsService.getSecretValue(projectId, objectStorage.secretId)
      );
      secretAccessKey = secret.value ?? '';
    }

    return {
      type: ConnectionType.ObjectStorage,
      endpoint: objectStorage.endpoint ?? '',
      accessKeyId: objectStorage.accessKey ?? '',
      secretAccessKey,
      region: objectStorage.region ?? '',
      bucket: objectStorage.bucket ?? '',
    };
  }

  /**
   * Extract the bucket name from a video URL. Handles AWS `s3://<bucket>/<key>`
   * and path-style `https://<endpoint>/<bucket>/<key>` (DO Spaces and other
   * S3-compatible providers). Returns null if no bucket can be determined.
   */
  private parseBucketName(url: string): string | null {
    const trimmed = String(url).trim();

    // AWS s3://<bucket>/<key>
    const s3 = /^s3:\/\/([^/]+)/i.exec(trimmed);
    if (s3) {
      return s3[1];
    }

    // Path-style http(s)://<endpoint>/<bucket>/<key>
    try {
      const firstSegment = new URL(trimmed).pathname.replace(/^\/+/, '').split('/')[0];
      return firstSegment || null;
    } catch {
      return null;
    }
  }

  /** Extract just the hostname from a stored endpoint (with or without scheme). */
  private hostnameOf(endpoint?: string | null): string {
    if (!endpoint) {
      return '';
    }
    try {
      const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(endpoint) ? endpoint : `https://${endpoint}`;
      return new URL(withScheme).hostname.toLowerCase();
    } catch {
      return '';
    }
  }

  /**
   * Find the ObjectStorage connection that owns `url` and build its ConnectionConfig
   * (with resolved credentials). Matches by bucket name; if several connections share
   * the bucket, disambiguates by provider vs URL scheme (s3:// -> AWS/S3, otherwise the
   * connection whose endpoint host matches the URL host). Throws if none match.
   */
  private async getConnectionConfigForUrl(url: string): Promise<ConnectionConfig> {
    const bucket = this.parseBucketName(url);
    if (!bucket) {
      throw new Error(`Could not determine bucket from video URL: ${url}`);
    }

    const projectId = this.connectionService.getCurrentProjectId();
    const objectStores = await firstValueFrom(this.objectStorageService.list(projectId));
    const matches = objectStores.filter(os => os.bucket === bucket);

    if (matches.length === 0) {
      throw new Error(`No ObjectStorage connection found for bucket "${bucket}"`);
    }

    const chosen = matches.length === 1 ? matches[0] : (this.disambiguateByUrl(url, matches) ?? matches[0]);
    if (chosen.connectionId == null) {
      throw new Error(`ObjectStorage connection for bucket "${bucket}" has no connectionId`);
    }

    // Re-fetch the single record: list() may return a sanitized shape, whereas
    // get() carries the credential fields (accessKey/secretId) we need.
    const objectStorage = await firstValueFrom(
      this.objectStorageService.get(projectId, chosen.connectionId)
    );
    return this.buildObjectStorageConfig(projectId, objectStorage);
  }

  /**
   * Pick the connection matching the URL scheme when several share a bucket name:
   * an `s3://` URL is AWS/S3; an http(s) URL matches the connection whose endpoint
   * host is contained in the URL host. Returns undefined when nothing matches.
   */
  private disambiguateByUrl(url: string, matches: ObjectStorageConnection[]): ObjectStorageConnection | undefined {
    const trimmed = String(url).trim();

    if (/^s3:\/\//i.test(trimmed)) {
      return matches.find(os => os.provider === ObjectStorageProvider.AWS || os.provider === ObjectStorageProvider.S3);
    }

    let host = '';
    try {
      host = new URL(trimmed).hostname.toLowerCase();
    } catch {
      return undefined;
    }
    return matches.find(os => {
      const endpointHost = this.hostnameOf(os.endpoint);
      return !!endpointHost && (host === endpointHost || host.endsWith(`.${endpointHost}`));
    });
  }

  private async performCloudOperation(
    action: 'upload' | 'download',
    { connectionName: connection, fileName }: StepParams['EHQ.uploadVideo'],
  ): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge, reject) => {
        this.getConnectionConfig(connection).then((config) => {
          bridge.receiveFromJs(JSON.stringify({
            set_connection_config: { config: config, file_name: fileName, action },
          }));
        }).catch((error) => {
          reject!(error);
        });
      },
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      });
  }

  // The step methods below take the params the shared parsers return (EHQ-10);
  // an omitted camera or file name arrives as ''.

  takePhoto({ cameraName }: StepParams['EHQ.takePhoto']): Promise<string> {
    if (!this.isQtEnvironmentAvailable()) {
      return this.handleNonQtTakePhoto();
    }
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({ take_snapshot: cameraName })),
      (data) => {
        const b64str = `data:image/jpeg;base64,${data}`;
        return b64str;
      },
      true // Use sendToJsBin handler for takePhoto
    );
  }

  async capturePhoto(params: StepParams['EHQ.capturePhoto'], context?: ChunkContext): Promise<string | null> {
    const { cameraName, message, details, okText, cancelText } = params;

    if (message || details || okText || cancelText) {
      const confirmed = await this.simpleUIService.confirm(
        {
          message: message ?? 'Take photo',
          details: details ?? '',
          okText: okText ?? 'Take Photo',
          cancelText: cancelText ?? 'Cancel',
        },
        context,
      );
      if (!confirmed) {
        return null;
      }
    }

    return this.takePhoto({ cameraName });
  }

  private handleNonQtTakePhoto(): Promise<string> {
    if (!Object.prototype.hasOwnProperty.call(window, 'ehq_loaded')
        || !window.webkit?.messageHandlers?.['ehq_takePhoto']) {
      return Promise.reject(new Error('takePhoto: native camera bridge not available in this environment.'));
    }
    return new Promise((resolve, reject) => {
      window.webkit!.messageHandlers['ehq_takePhoto'].postMessage({ action: 'takePhoto' }).then((response: any) => {
        const b64str = `data:image/jpeg;base64,${response}`;
        resolve(b64str);
      }).catch((error: any) => {
        reject(new Error('Failed to take photo: ' + error));
      });
    });
  }

  getCameraList(_params?: StepParams['EHQ.getCameraList']): Promise<string[]> {
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({ get_camera_list: true })),
      (data) => {
        const resp = JSON.parse(data);
        return resp.cameras;
      }
    );
  }

  selectCamera({ cameraName }: StepParams['EHQ.selectCamera']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({ select_camera: cameraName })),
      (data) => JSON.parse(data)
    );
  }

  releaseCamera({ cameraName }: StepParams['EHQ.releaseCamera']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({ release_camera: cameraName })),
      (data) => JSON.parse(data)
    );
  }

  selectROI(_params?: StepParams['EHQ.selectROI']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        select_roi: ""
      })),
      (data) => JSON.parse(data)
    );
  }

  startVideoRecording({ cameraName, fileName }: StepParams['EHQ.startVideoRecording']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        start_video_recording: { file_name: fileName, camera_name: cameraName }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  stopVideoRecording({ cameraName }: StepParams['EHQ.stopVideoRecording']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        stop_video_recording: { camera_name: cameraName }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  uploadVideo(params: StepParams['EHQ.uploadVideo']): Promise<string> {
    return this.performCloudOperation('upload', params);
  }

  downloadVideo(params: StepParams['EHQ.downloadVideo']): Promise<string> {
    return this.performCloudOperation('download', params);
  }

  pipelinePreview(params: StepParams['EHQ.pipelinePreview']): Promise<string> {
    const { inputVideoPath, pipelineName, textPrompt, startFrameNumber, endFrameNumber } = params;
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        pipeline_preview: {
          video_path: inputVideoPath,
          start_frame_number: startFrameNumber,
          end_frame_number: endFrameNumber,
          pipeline_name: pipelineName,
          text_prompt: textPrompt

        }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  pipelineProcess(params: StepParams['EHQ.pipelineProcess']): Promise<string> {
    const { inputVideoPath, outputVideoPath, pipelineName, textPrompt, startFrameNumber, endFrameNumber } = params;
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        pipeline_process: {
          input_video_path: inputVideoPath,
          output_video_path: outputVideoPath,
          start_frame_number: startFrameNumber,
          end_frame_number: endFrameNumber,
          pipeline_name: pipelineName,
          text_prompt: textPrompt
        }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  pipelinePreviewCamera(params: StepParams['EHQ.pipelinePreviewCamera']): Promise<string> {
    const { cameraName, pipelineName, textPrompt } = params;
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        pipeline_preview_camera: {
          camera_name: cameraName,
          pipeline_name: pipelineName,
          text_prompt: textPrompt
        }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  pipelineRecordCamera(params: StepParams['EHQ.pipelineRecordCamera']): Promise<string> {
    const { cameraName, outputVideoPath, pipelineName, textPrompt } = params;
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        pipeline_record_camera: {
          camera_name: cameraName,
          output_video_path: outputVideoPath,
          pipeline_name: pipelineName,
          text_prompt: textPrompt
        }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  pipelineStopCamera({ cameraName }: StepParams['EHQ.pipelineStopCamera']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        pipeline_stop_camera: { camera_name: cameraName }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  startTracking(params: StepParams['EHQ.startTracking']): Promise<string> {
    const { cameraName, refCameraName, objectClass } = params;
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        start_tracking: {
          camera_name: cameraName,
          ref_camera_name: refCameraName,
          object_class: objectClass
        }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  stopTracking({ cameraName }: StepParams['EHQ.stopTracking']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        stop_tracking: {
          camera_name: cameraName
        }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  startCloudRecording({ cameraName, connectionName }: StepParams['EHQ.startCloudRecording']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge, reject) => {
        this.getConnectionConfig(connectionName).then((config) => {
          bridge.receiveFromJs(JSON.stringify({
            start_cloud_recording: { camera_name: cameraName, config }
          }));
        }).catch((error) => {
          reject!(error);
        });
      },
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  stopCloudRecording({ cameraName }: StepParams['EHQ.stopCloudRecording']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge) => bridge.receiveFromJs(JSON.stringify({
        stop_cloud_recording: { camera_name: cameraName }
      })),
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }

  /**
   * Start the native proxy that serves a video from a non-public bucket and
   * resolve with the local proxied URL to play. `url` is the video reference
   * (s3:// or path-style https); the matching ObjectStorage connection is
   * resolved by bucket and its ConnectionConfig is handed to the proxy.
   */
  proxyUrlForSource({ url }: StepParams['EHQ.proxyUrlForSource']): Promise<string> {
    return this.createQWebChannelPromise(
      (bridge, reject) => {
        this.getConnectionConfigForUrl(url).then((config) => {
          bridge.receiveFromJs(JSON.stringify({
            start_proxy_for_url: { url, config }
          }));
        }).catch((error) => {
          reject!(error);
        });
      },
      (data) => {
        const response = JSON.parse(data);
        if (!response.success) {
          return Promise.reject(new Error(response.message));
        }
        return response.result;
      }
    );
  }
}
